import type { SearchContentType } from '~/features/analytix'

import { api } from '@setplex/tria-api'
import {
  createEffect,
  createEvent,
  createStore,
  sample,
  scopeBind,
} from 'effector'
import { entitiesHybridSearchModel } from '~/entities/hybrid-search'
import { model as session } from '~/entities/session'
import {
  CdnInfoEvent,
  cdnInfo,
  contentActionTracked,
  ContentActionsUiActionEvent,
  FavoriteAddEvent,
  FavoriteRemoveEvent,
  MotnUiActionEvent,
  motnDetailsOpened,
  motnProviderOpened,
  motnStreamingOptionsOpened,
  PlayerRecommendationsUiActionEvent,
  playerRecommendationsShownTracked,
  PlayerCloseEvent,
  PlayerMountedMessage,
  PlayerOpenEvent,
  PlayStartEvent,
  PlayStopEvent,
  playStart,
  playStop,
  SEARCH_CONTENT_TYPE,
  SearchEvent,
  searchResultClickTracked,
  SearchResultUiActionEvent,
  SessionEndEvent,
  SessionStartEvent,
  StreamInfoEvent,
  streamInfo,
} from '~/features/analytix'
import { NavigationWatcher } from '~/features/analytix/routing'
import { model as favorites } from '~/features/favorites'
import { aye } from '~/shared/helpers'
import { model as mq, PLAYER } from '~/shared/mq'
import { emit } from './wrapper'

/**
 * Attaches all process-level bridges from app signals to Analytix events.
 */
export const wire = createEvent()

//
// Manage user info and session start/end events
// Event `session_start`: subscriber session started
// Event `session_end`: subscriber session ended
//

/**
 * Best-effort session shutdown when browser tab is closing.
 * Note: in practice browser teardown is fast, so this hook is a fallback only.
 */
const watchCloseFx = createEffect(() => {
  const _emit = scopeBind(emit, { safe: true })
  window.addEventListener('beforeunload', () => _emit(new SessionEndEvent()))
})

// listen for page unload to end session
sample({
  clock: wire,
  target: watchCloseFx,
})

const $routingWatcher = createStore<NavigationWatcher | null>(null)

// Fires initial navigation event on app start, from constructor, and then starts watching routes.
const cretateRoutingWatcherFx = createEffect(() => new NavigationWatcher())

// Start Navigation Watcher to track route changes and emit navigation events asap.
sample({
  clock: wire,
  target: cretateRoutingWatcherFx,
})

// Save watcher link.
sample({
  clock: cretateRoutingWatcherFx.doneData,
  target: $routingWatcher,
})

// if there was previous session, which is different from current session, emit `session_end` event
sample({
  clock: [wire, session.$session],
  source: { previous: session.$previousSession, session: session.$session },
  filter: ({ previous, session }) =>
    previous != null && previous.accountNumber !== session?.accountNumber,
  fn: () => new SessionEndEvent(),
  target: emit,
})

// if there is new session, which is different from previous session, emit `session_start` event
sample({
  clock: [wire, session.$session],
  source: { previous: session.$previousSession, session: session.$session },
  filter: ({ previous, session }) =>
    session != null && session.accountNumber !== previous?.accountNumber,
  fn: () => new SessionStartEvent(),
  target: emit,
})

//
// Manage favorite events
// Event `favorite_add`: Subscriber added content to favorite
// Event `favorite_remove`: Subscriber removed content from favorite
//

sample({
  clock: [
    favorites.favoriteTvChannelFx.done,
    favorites.favoriteVodFx.done,
    favorites.favoriteTvShowFx.done,
  ],
  fn: ({ params }) =>
    params.favorite //
      ? new FavoriteAddEvent()
      : new FavoriteRemoveEvent(),
  target: emit,
})

//
// Event `player_open`: Video Player Open
//

sample({
  clock: mq.on(PlayerMountedMessage),
  filter: (payload) => payload.mounted,
  fn: () => new PlayerOpenEvent(),
  target: emit,
})

// Synthetic event to close player/play gates on player dispose.
// There is no backend `player_close` event; this only controls queue gates.
sample({
  clock: mq.on(`${PLAYER}/dispose`),
  fn: () => new PlayerCloseEvent(),
  target: emit,
})

//
// Event `play_start`: Subscriber started to watch content
//

sample({
  clock: playStart,
  fn: ({ reason, snapshot, isFirstPlay }) =>
    new PlayStartEvent(reason, snapshot, isFirstPlay),
  target: emit,
})

//
// Event `play_stop`: Subscriber stopped or paused to watch content
//

sample({
  clock: playStop,
  fn: ({ reason, snapshot }) => new PlayStopEvent(reason, snapshot),
  target: emit,
})

//
// Event `search`: Subscriber used search (hybrid)
//

sample({
  clock: [
    entitiesHybridSearchModel.getHybridSearchFx.done,
    api.globalSearch.getSearchFx.done,
  ],
  filter: ({ params }) =>
    Boolean(params?.q?.trim()) &&
    aye(params.typeOfSearch) &&
    Object.values(SEARCH_CONTENT_TYPE).includes(
      params.typeOfSearch as SearchContentType
    ),
  fn: ({ params }) =>
    new SearchEvent(
      params.q!.trim(),
      params.typeOfSearch! as SearchContentType
    ),
  target: emit,
})

//
// Event `ui_action`: Subscriber selected a search result (ET-1)
//

sample({
  clock: searchResultClickTracked,
  fn: (params) => new SearchResultUiActionEvent(params),
  target: emit,
})

//
// Event `cdn_info`: CDN information collected on loadedmetadata
//

sample({
  clock: cdnInfo,
  fn: () => new CdnInfoEvent(),
  target: emit,
})

//
// Event `stream_info`: Periodic stream information
//

sample({
  clock: streamInfo,
  fn: () => new StreamInfoEvent(),
  target: emit,
})

//
// Event `ui_action`: Some UI user actions
// For "Movie of the Night" feature
//

sample({
  clock: [motnDetailsOpened, motnProviderOpened, motnStreamingOptionsOpened],
  fn: (params) => new MotnUiActionEvent(params),
  target: emit,
})

//
// Event `ui_action`: Content actions feature
//

sample({
  clock: contentActionTracked,
  fn: (params) => new ContentActionsUiActionEvent(params),
  target: emit,
})

//
// Event `ui_action`: Player recommendations row shown
//

sample({
  clock: playerRecommendationsShownTracked,
  fn: (params) => new PlayerRecommendationsUiActionEvent(params),
  target: emit,
})
